import { Analytics } from '@cobliteam/analytics';
import type { UUID } from '@cobliteam/common-types';
import { useActivationTasks } from '@cobliteam/hooks';
import Button from '@cobliteam/ui/components/Button';
import Card, { CardContent } from '@cobliteam/ui/components/Card';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Icon from '@cobliteam/ui/icons';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import type { ActivationTasksTypes } from './ActivationTasksCommon';

interface CardItemProps {
  title: string;
  description: string;
  url: string;
  checked: boolean;
  step: ActivationTasksTypes;
  stepId: UUID;
  onCheckboxChange: (checked: boolean) => void;
  titleColor?: 'secondary' | 'primary';
}

const ActivationTaskCard = ({ title, description, url, checked, step, stepId, onCheckboxChange, titleColor = 'secondary' }: CardItemProps) => {
  const history = useHistory();

  const { useUpdateUserActivationTask } = useActivationTasks();
  const { mutate: updateUserActivationTask } = useUpdateUserActivationTask();

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCheckboxChange = useCallback(
    (newChecked: boolean) => {
      onCheckboxChange(newChecked);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        updateUserActivationTask({ id: stepId, finished: newChecked });
      }, 500);
    },
    [onCheckboxChange, stepId, updateUserActivationTask]
  );

  const handleClick = useCallback(() => {
    Analytics.track('Onboarding Checklist - ActivationTask card clicked', { step: step });
    history.push(url);
  }, [history, url, step]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Card className={`flex max-h-[44px] flex-col justify-center ${checked ? 'bg-grey-3' : ''}`}>
      <CardContent className='!px-3 !py-0 flex h-full flex-row items-center gap-4'>
        <Checkbox onCheckedChange={handleCheckboxChange} checked={checked} className='ml-2' />

        <div className='flex flex-row items-center gap-2'>
          <Typography
            variant='text-lg-semibold'
            color='secondary'
            className={`cursor-pointer hover:underline ${titleColor === 'primary' ? 'text-primary-11' : ''} ${checked ? 'line-through' : ''}`}
            onClick={handleClick}
          >
            {title}
          </Typography>
          <Tooltip content={description}>
            <Icon name='info' className='text-gray-11' />
          </Tooltip>
        </div>

        <Button variant='ghost' color='primary' onClick={handleClick} className='ml-auto'>
          <Icon name='arrow-right' size='md' className='text-primary-11' />
        </Button>
      </CardContent>
    </Card>
  );
};

export default memo(ActivationTaskCard);
